import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';
import { CheckCircle, DollarSign, Users, TrendingUp, Gift } from 'lucide-react';

export default function AffiliateApplication() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website_url: '',
    portfolio_url: '',
    promotion_method: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.promotion_method) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await api.post('/public/affiliate/apply', formData);
      setSubmitted(true);
      toast({
        title: "Application Submitted!",
        description: "We'll review your application within 24-48 hours."
      });
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/10 backdrop-blur-lg border-white/20">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Application Received!</h2>
            <p className="text-slate-300 mb-6">
              Thank you for applying to become a NexusCMS Affiliate Partner. We'll review your application and get back to you within 24-48 hours.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Become a NexusCMS <span className="text-purple-400">Affiliate Partner</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Earn 20% lifetime commission on every customer you refer. No limits, no caps.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-12 max-w-4xl mx-auto">
          <Card className="bg-white/5 border-white/10 text-center p-6">
            <DollarSign className="w-10 h-10 text-green-400 mx-auto mb-3" />
            <h3 className="text-white font-semibold mb-1">20% Lifetime</h3>
            <p className="text-slate-400 text-sm">Commission on every payment, forever</p>
          </Card>
          
          <Card className="bg-white/5 border-white/10 text-center p-6">
            <TrendingUp className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <h3 className="text-white font-semibold mb-1">Recurring Revenue</h3>
            <p className="text-slate-400 text-sm">Earn as long as they stay subscribed</p>
          </Card>
          
          <Card className="bg-white/5 border-white/10 text-center p-6">
            <Users className="w-10 h-10 text-purple-400 mx-auto mb-3" />
            <h3 className="text-white font-semibold mb-1">30-Day Cookies</h3>
            <p className="text-slate-400 text-sm">Referrals tracked for 30 days</p>
          </Card>
          
          <Card className="bg-white/5 border-white/10 text-center p-6">
            <Gift className="w-10 h-10 text-pink-400 mx-auto mb-3" />
            <h3 className="text-white font-semibold mb-1">Free to Join</h3>
            <p className="text-slate-400 text-sm">No cost, no commitment</p>
          </Card>
        </div>

        {/* Application Form */}
        <div className="max-w-xl mx-auto">
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardHeader>
              <CardTitle className="text-white">Apply Now</CardTitle>
              <CardDescription className="text-slate-300">
                Tell us a bit about yourself and how you plan to promote NexusCMS.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                    required
                    data-testid="affiliate-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                    required
                    data-testid="affiliate-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website_url" className="text-white">Website URL</Label>
                  <Input
                    id="website_url"
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    placeholder="https://yourwebsite.com"
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                    data-testid="affiliate-website-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="portfolio_url" className="text-white">Portfolio URL</Label>
                  <Input
                    id="portfolio_url"
                    type="url"
                    value={formData.portfolio_url}
                    onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                    placeholder="https://dribbble.com/yourprofile"
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                    data-testid="affiliate-portfolio-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="promotion_method" className="text-white">How will you promote NexusCMS? *</Label>
                  <Textarea
                    id="promotion_method"
                    value={formData.promotion_method}
                    onChange={(e) => setFormData({ ...formData, promotion_method: e.target.value })}
                    placeholder="E.g., I run a design blog with 10k monthly visitors, I'll write tutorials and reviews..."
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 min-h-[100px]"
                    required
                    data-testid="affiliate-promotion-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={loading}
                  data-testid="affiliate-submit-btn"
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </Button>

                <p className="text-xs text-slate-400 text-center mt-4">
                  By applying, you agree to our affiliate terms and conditions.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-purple-600 text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">1</div>
              <h3 className="text-white font-semibold mb-2">Apply & Get Approved</h3>
              <p className="text-slate-400 text-sm">Submit your application. We review within 24-48 hours.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-purple-600 text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">2</div>
              <h3 className="text-white font-semibold mb-2">Share Your Link</h3>
              <p className="text-slate-400 text-sm">Get your unique referral link and start sharing.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-purple-600 text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">3</div>
              <h3 className="text-white font-semibold mb-2">Earn Commission</h3>
              <p className="text-slate-400 text-sm">Earn 20% of every payment, for the lifetime of the customer.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
